import React, { useState, useEffect } from 'react';
import { supabase } from '../supabase';
import { CheckCircle, Clock, MessageSquare, Search, User, X, Save, AlertCircle, Loader2, ChevronRight } from 'lucide-react';

interface SupportTicket {
  id: string;
  created_at: string;
  title: string;
  message: string;
  status: 'open' | 'progress' | 'closed';
  admin_notes: string | null;
  profiles?: { name: string; email: string };
  containers?: { name: string };
}

const SuperAdminSupportPanel: React.FC = () => {
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Modal State
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [adminNoteDraft, setAdminNoteDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState<'open' | 'progress' | 'closed'>('open');
  const [updating, setUpdating] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const fetchTickets = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('support_tickets')
      .select('*, profiles:user_id(name, email), containers:container_id(name)')
      .order('created_at', { ascending: false });
    
    if (!error && data) {
      setTickets(data as SupportTicket[]);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchTickets();
  }, []);

  const openTicketModal = (ticket: SupportTicket) => {
    setSelectedTicket(ticket);
    setAdminNoteDraft(ticket.admin_notes || '');
    setStatusDraft(ticket.status);
    setErrorMsg(null);
    setSuccessMsg(null);
  };

  const handleUpdateTicket = async (updates: Partial<SupportTicket>) => {
    if (!selectedTicket) return;
    setUpdating(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const { error } = await supabase
        .from('support_tickets')
        .update(updates)
        .eq('id', selectedTicket.id);

      if (error) {
        console.error('[SupportTickets] Update failed:', error);
        throw error;
      }

      // Update Optimista
      const updatedTicket = { ...selectedTicket, ...updates };
      setTickets(prev => prev.map(t => t.id === selectedTicket.id ? { ...t, ...updates } : t));
      setSelectedTicket(updatedTicket);
      
      // Sincroniza drafts caso o update tenha vindo de um botão direto
      if (updates.status) setStatusDraft(updates.status);
      if (updates.admin_notes !== undefined) setAdminNoteDraft(updates.admin_notes || '');

      setSuccessMsg('Ticket atualizado com sucesso.');
      
      // Auto-hide success message
      setTimeout(() => setSuccessMsg(null), 3000);

    } catch (err: any) {
      setErrorMsg(err.message || 'Erro ao atualizar ticket.');
    } finally {
      setUpdating(false);
    }
  };

  const filteredTickets = tickets.filter(t => 
    t.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.profiles?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.containers?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'progress': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'closed': return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      default: return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'open': return 'Aberto';
      case 'progress': return 'Em Análise';
      case 'closed': return 'Resolvido';
      default: return status;
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in">
       {/* Header */}
       <div className="flex justify-between items-center">
          <h3 className="text-xl font-black text-navy-950 dark:text-white">Tickets de Suporte</h3>
          <div className="relative">
             <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"/>
             <input 
               value={searchTerm}
               onChange={e => setSearchTerm(e.target.value)}
               placeholder="Buscar ticket..."
               className="pl-9 pr-4 py-2 bg-white dark:bg-navy-800 border border-slate-200 dark:border-navy-700 rounded-xl text-xs font-bold outline-none focus:ring-2 focus:ring-parliament-600 dark:text-white transition-all"
             />
          </div>
       </div>

       {/* Lista */}
       <div className="bg-white dark:bg-navy-900 border border-neutral-200 dark:border-navy-800 rounded-[32px] overflow-hidden shadow-sm">
          <table className="w-full text-left">
             <thead className="bg-slate-50 dark:bg-navy-950 text-slate-400 text-[9px] font-black uppercase tracking-widest border-b border-neutral-200 dark:border-navy-800">
                <tr>
                   <th className="px-6 py-4">Status</th>
                   <th className="px-6 py-4">Usuário / Gabinete</th>
                   <th className="px-6 py-4">Assunto</th>
                   <th className="px-6 py-4">Data</th>
                   <th className="px-6 py-4 text-right">Ações</th>
                </tr>
             </thead>
             <tbody className="divide-y divide-neutral-100 dark:divide-navy-800">
                {filteredTickets.map(ticket => (
                   <tr key={ticket.id} onClick={() => openTicketModal(ticket)} className="hover:bg-slate-50 dark:hover:bg-navy-950 transition-colors cursor-pointer group">
                      <td className="px-6 py-4">
                         <span className={`px-2.5 py-1 rounded-md text-[8px] font-black uppercase tracking-widest border ${getStatusColor(ticket.status)}`}>
                            {getStatusLabel(ticket.status)}
                         </span>
                      </td>
                      <td className="px-6 py-4">
                         <div className="flex items-center gap-2">
                            <div className="w-6 h-6 bg-slate-200 dark:bg-navy-800 rounded-full flex items-center justify-center text-[10px] font-bold text-slate-600 dark:text-slate-400">
                               <User size={12}/>
                            </div>
                            <div>
                               <p className="text-xs font-bold text-navy-900 dark:text-white">{ticket.profiles?.name || 'Desconhecido'}</p>
                               <p className="text-[9px] text-slate-400">{ticket.containers?.name || 'Sem Gabinete'}</p>
                            </div>
                         </div>
                      </td>
                      <td className="px-6 py-4">
                         <p className="text-xs font-bold text-navy-900 dark:text-white">{ticket.title}</p>
                         <p className="text-[10px] text-slate-500 truncate max-w-[200px]">{ticket.message}</p>
                      </td>
                      <td className="px-6 py-4">
                         <div className="flex items-center gap-1 text-[10px] font-medium text-slate-400">
                            <Clock size={10}/> {new Date(ticket.created_at).toLocaleDateString()}
                         </div>
                      </td>
                      <td className="px-6 py-4 text-right">
                         <button className="p-2 text-slate-300 hover:text-parliament-600 dark:hover:text-white transition-colors">
                            <ChevronRight size={16}/>
                         </button>
                      </td>
                   </tr>
                ))}
                {filteredTickets.length === 0 && (
                   <tr><td colSpan={5} className="py-10 text-center text-xs text-slate-400 font-bold">Nenhum ticket encontrado.</td></tr>
                )}
             </tbody>
          </table>
       </div>

       {/* Modal de Detalhes */}
       {selectedTicket && (
         <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-navy-950/80 backdrop-blur-sm animate-in fade-in" onClick={() => setSelectedTicket(null)}>
            <div className="bg-white dark:bg-navy-900 w-full max-w-2xl rounded-[32px] shadow-2xl flex flex-col overflow-hidden border border-neutral-200 dark:border-navy-800 relative max-h-[90vh]" onClick={e => e.stopPropagation()}>
               
               <div className="px-8 py-6 border-b border-neutral-100 dark:border-navy-800 flex items-center justify-between bg-white dark:bg-navy-900">
                  <div className="flex items-center gap-3">
                     <div className={`p-2 rounded-xl border ${getStatusColor(selectedTicket.status)}`}>
                        <MessageSquare size={20}/>
                     </div>
                     <div>
                        <h3 className="text-lg font-black text-navy-950 dark:text-white">{selectedTicket.title}</h3>
                        <p className="text-xs text-slate-500 dark:text-slate-400">Ticket #{selectedTicket.id.substring(0,8)}</p>
                     </div>
                  </div>
                  <button onClick={() => setSelectedTicket(null)} className="p-2 hover:bg-neutral-100 dark:hover:bg-navy-800 rounded-full text-slate-400 transition-colors"><X size={20}/></button>
               </div>

               <div className="p-8 overflow-y-auto custom-scrollbar flex-1 space-y-8 bg-slate-50/50 dark:bg-navy-950/50">
                  {/* Feedback UI */}
                  {errorMsg && (
                     <div className="p-4 bg-red-50 text-red-600 rounded-xl text-xs font-bold flex items-center gap-2 border border-red-100">
                        <AlertCircle size={14}/> {errorMsg}
                     </div>
                  )}
                  {successMsg && (
                     <div className="p-4 bg-emerald-50 text-emerald-600 rounded-xl text-xs font-bold flex items-center gap-2 border border-emerald-100">
                        <CheckCircle size={14}/> {successMsg}
                     </div>
                  )}

                  <div className="grid grid-cols-2 gap-6">
                     <div className="p-4 bg-white dark:bg-navy-800 rounded-2xl border border-neutral-100 dark:border-navy-700">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Solicitante</p>
                        <p className="text-sm font-bold text-navy-900 dark:text-white">{selectedTicket.profiles?.name}</p>
                        <p className="text-xs text-slate-500">{selectedTicket.profiles?.email}</p>
                     </div>
                     <div className="p-4 bg-white dark:bg-navy-800 rounded-2xl border border-neutral-100 dark:border-navy-700">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Gabinete / Origem</p>
                        <p className="text-sm font-bold text-navy-900 dark:text-white">{selectedTicket.containers?.name || 'Gabinete não identificado'}</p>
                        <p className="text-xs text-slate-500">{new Date(selectedTicket.created_at).toLocaleString()}</p>
                     </div>
                  </div>

                  <div className="space-y-2">
                     <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest ml-1">Descrição do Problema</p>
                     <div className="p-6 bg-white dark:bg-navy-800 rounded-2xl border border-neutral-100 dark:border-navy-700 text-sm font-medium text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-wrap max-h-60 overflow-y-auto custom-scrollbar">
                        {selectedTicket.message}
                     </div>
                  </div>

                  <div className="space-y-4 pt-6 border-t border-neutral-200 dark:border-navy-800">
                     <div className="flex items-center justify-between">
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-2"><Save size={12}/> Área Administrativa</p>
                        <div className="flex items-center gap-2">
                           <span className="text-[10px] font-bold text-slate-500">Status:</span>
                           <select 
                              value={statusDraft} 
                              onChange={(e) => setStatusDraft(e.target.value as any)}
                              className="bg-white dark:bg-navy-800 border border-neutral-200 dark:border-navy-700 rounded-lg text-xs font-bold px-2 py-1 outline-none focus:ring-2 focus:ring-parliament-600 dark:text-white"
                           >
                              <option value="open">Aberto</option>
                              <option value="progress">Em Análise</option>
                              <option value="closed">Resolvido</option>
                           </select>
                        </div>
                     </div>
                     
                     <textarea 
                        value={adminNoteDraft}
                        onChange={(e) => setAdminNoteDraft(e.target.value)}
                        placeholder="Adicione notas internas sobre a resolução..."
                        className="w-full h-32 bg-white dark:bg-navy-800 border border-neutral-200 dark:border-navy-700 rounded-2xl p-4 text-sm font-medium outline-none focus:ring-2 focus:ring-parliament-600 dark:text-white resize-none"
                     />
                  </div>
               </div>

               <div className="p-6 bg-white dark:bg-navy-900 border-t border-neutral-100 dark:border-navy-800 flex justify-between gap-4">
                  {selectedTicket.status !== 'closed' ? (
                     <button 
                       onClick={() => handleUpdateTicket({ status: 'closed' })}
                       disabled={updating}
                       className="px-6 py-3 bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-emerald-100 transition-all border border-emerald-100 dark:border-emerald-800"
                     >
                        Marcar como Resolvido
                     </button>
                  ) : (
                     <div className="flex items-center gap-2 px-6 py-3 text-emerald-600 font-bold text-xs bg-emerald-50 rounded-xl border border-emerald-100">
                        <CheckCircle size={16}/> Ticket Fechado
                     </div>
                  )}

                  <div className="flex gap-3">
                     <button onClick={() => setSelectedTicket(null)} className="px-6 py-3 text-slate-400 font-bold text-xs uppercase hover:bg-slate-50 dark:hover:bg-navy-800 rounded-xl transition-all">Cancelar</button>
                     <button 
                        onClick={() => handleUpdateTicket({ admin_notes: adminNoteDraft, status: statusDraft })}
                        disabled={updating}
                        className="bg-navy-950 dark:bg-parliament-600 text-white px-8 py-3 rounded-xl font-black text-xs uppercase tracking-widest shadow-lg flex items-center gap-2 hover:scale-[1.02] transition-all disabled:opacity-50"
                     >
                        {updating ? <Loader2 className="animate-spin" size={16}/> : <Save size={16}/>} Salvar Alterações
                     </button>
                  </div>
               </div>

            </div>
         </div>
       )}
    </div>
  );
};

export default SuperAdminSupportPanel;